import React, { useState } from "react";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";

import { DashboardPath } from "../../../types/org-dashboard";

export interface DashboardCardMenuProps {
  path: DashboardPath;
  /** How many cards sit in this card's own stack, for the move limits. */
  stackLength: number;
  columnCount: number;
  onMoveWithinStack: (path: DashboardPath, offset: -1 | 1) => void;
  onMoveToColumn: (path: DashboardPath, column: number) => void;
  onMoveOutOfSplit: (path: DashboardPath) => void;
  onMoveAcrossSplit: (path: DashboardPath) => void;
  onRemove: (path: DashboardPath) => void;
}

/**
 * Layout controls for one card. They live in a kebab rather than inline so the
 * card header stays a single fixed-height row at every column width.
 */
const DashboardCardMenu = ({
  path,
  stackLength,
  columnCount,
  onMoveWithinStack,
  onMoveToColumn,
  onMoveOutOfSplit,
  onMoveAcrossSplit,
  onRemove,
}: DashboardCardMenuProps) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = () => setAnchor(null);
  const run = (action: () => void) => () => {
    action();
    close();
  };

  const index = path.part === undefined ? path.item : (path.card ?? 0);
  const inSplit = path.part !== undefined;

  return (
    <>
      <Tooltip title="Card layout options">
        <IconButton
          size="small"
          onClick={(event) => setAnchor(event.currentTarget)}
          aria-label="Card layout options"
          // Every card in a split shares path.item (the split's index), so the
          // part and card indices have to join the id to keep it unique.
          data-testid={
            inSplit
              ? `dashboard-card-menu-${path.column}-${path.item}-${path.part}-${index}`
              : `dashboard-card-menu-${path.column}-${path.item}`
          }
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        <MenuItem
          disabled={index === 0}
          onClick={run(() => onMoveWithinStack(path, -1))}
        >
          <ListItemText>Move up</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={index >= stackLength - 1}
          onClick={run(() => onMoveWithinStack(path, 1))}
        >
          <ListItemText>Move down</ListItemText>
        </MenuItem>

        {/* Arrays rather than fragments: MUI's Menu will not accept a Fragment. */}
        {inSplit
          ? [
              <MenuItem
                key="across"
                onClick={run(() => onMoveAcrossSplit(path))}
              >
                <ListItemText>Move to other side of split</ListItemText>
              </MenuItem>,
              <MenuItem key="out" onClick={run(() => onMoveOutOfSplit(path))}>
                <ListItemText>Move out of split</ListItemText>
              </MenuItem>,
            ]
          : [
              <MenuItem
                key="previous"
                disabled={path.column === 0}
                onClick={run(() => onMoveToColumn(path, path.column - 1))}
              >
                <ListItemText>{`Move to column ${path.column}`}</ListItemText>
              </MenuItem>,
              <MenuItem
                key="next"
                disabled={path.column >= columnCount - 1}
                onClick={run(() => onMoveToColumn(path, path.column + 1))}
              >
                <ListItemText>{`Move to column ${
                  path.column + 2
                }`}</ListItemText>
              </MenuItem>,
            ]}

        <Divider />
        <MenuItem onClick={run(() => onRemove(path))}>
          <ListItemText sx={{ color: "error.main" }}>
            Remove from dashboard
          </ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default DashboardCardMenu;
